import { createContext, ReactNode, useContext, useState } from "react";
import type { errorState } from "../../hooks/useErrorDialog";

export interface dtsAppContextProviderProps {
  children: ReactNode;
}

//並び順のリテラル型
export type SortOrder = "desc" | "asc";

//フィルター項目のリテラル型
export type FilterItem =
  | ""
  | "subjectName"
  | "model"
  | "machineNumber"
  | "status";

export const DtsAppContextProvider: React.FC<dtsAppContextProviderProps> = ({
  children,
}) => {
  //DtsAppContextからdacを接頭辞とした命名規則とする
  const [dacUserId, setDacUserId] = useState<string>(""); //ユーザーID
  const [dacSubjectName, setDacSubjectName] = useState<string>(""); //案件名
  const [dacModel, setDacModel] = useState<string>(""); //型式
  const [dacMachineNumber, setDacMachineNumber] = useState<string>(""); //機番
  const [dacDtcSearchData, setDacDtcSearchData] = useState<string>(""); //DTC Searchに渡す型式データ
  const [dacModelType, setDacModelType] = useState<string>(""); //選択した型式が何階層目のものか
  const [dacOccurrenceTiming, setDacOccurrenceTiming] = useState<number>(0); //発生タイミング
  const [dacInferenceFactor, setDacInferenceFactor] = useState<string>(""); //推定要因項目(JSON string)
  const [dacSearchHistoryId, setDacSearchHistoryId] = useState<string>(""); //検索履歴情報ID
  const [dacLanguage, setDacLanguage] = useState<string>(""); //言語
  const [errorState, seterrorState] = useState<errorState>({
    isShowErrorDialog: false,
    errorTitle: "",
    errorMessage: "",
    onOk: "",
  }); // エラー情報(Contextでのみ保持・ローカルストレージで保持しない)
  const [loading, setLoading] = useState<boolean>(false); // ローディング表示(Contextでのみ保持・ローカルストレージで保持しない)
  const [dacSearchHistorySortOrder, setDacSearchHistorySortOrder] =
    useState<SortOrder>("desc"); //並び順(検索履歴一覧)
  const [dacSearchHistoryPageNum, setDacSearchHistoryPageNum] =
    useState<number>(0); //ページ番号(検索履歴一覧)
  const [dacFilterItem, setDacFilterItem] = useState<FilterItem>(""); //フィルター項目
  const [dacFilterCriteria, setDacFilterCriteria] = useState<string>(""); //フィルター条件
  const [dacFavoriteSortOrder, setDacFavoriteSortOrder] =
    useState<SortOrder>("desc"); //並び順(お気に入り情報一覧)
  const [dacFavoritePageNum, setDacFavoritePageNum] = useState<number>(0); //ページ番号(お気に入り情報一覧)
  const defaultSplash =
    sessionStorage.getItem("splash") === String(true) ? true : false;
  const [splash, setSplash] = useState<boolean>(defaultSplash); //スプラッシュ画面表示(セッションストレージでも保持)

  return (
    <DtsAppContext.Provider
      value={{
        dacUserId,
        setDacUserId,
        dacSubjectName,
        setDacSubjectName,
        dacModel,
        setDacModel,
        dacMachineNumber,
        setDacMachineNumber,
        dacDtcSearchData,
        setDacDtcSearchData,
        dacModelType,
        setDacModelType,
        dacInferenceFactor,
        setDacInferenceFactor,
        dacSearchHistoryId,
        setDacSearchHistoryId,
        dacOccurrenceTiming,
        setDacOccurrenceTiming,
        dacLanguage,
        setDacLanguage,
        errorState,
        seterrorState,
        loading,
        setLoading,
        dacSearchHistorySortOrder,
        setDacSearchHistorySortOrder,
        dacSearchHistoryPageNum,
        setDacSearchHistoryPageNum,
        dacFilterItem,
        setDacFilterItem,
        dacFilterCriteria,
        setDacFilterCriteria,
        dacFavoriteSortOrder,
        setDacFavoriteSortOrder,
        dacFavoritePageNum,
        setDacFavoritePageNum,
        splash,
        setSplash,
      }}
    >
      {children}
    </DtsAppContext.Provider>
  );
};

interface dtsAppContextProps {
  dacUserId: string;
  setDacUserId: React.Dispatch<React.SetStateAction<string>>;
  dacSubjectName: string;
  setDacSubjectName: React.Dispatch<React.SetStateAction<string>>;
  dacModel: string;
  setDacModel: React.Dispatch<React.SetStateAction<string>>;
  dacMachineNumber: string;
  setDacMachineNumber: React.Dispatch<React.SetStateAction<string>>;
  dacDtcSearchData: string;
  setDacDtcSearchData: React.Dispatch<React.SetStateAction<string>>;
  dacModelType: string;
  setDacModelType: React.Dispatch<React.SetStateAction<string>>;
  dacInferenceFactor: string;
  setDacInferenceFactor: React.Dispatch<React.SetStateAction<string>>;
  dacSearchHistoryId: string;
  setDacSearchHistoryId: React.Dispatch<React.SetStateAction<string>>;
  dacLanguage: string;
  setDacLanguage: React.Dispatch<React.SetStateAction<string>>;
  dacOccurrenceTiming: number;
  setDacOccurrenceTiming: React.Dispatch<React.SetStateAction<number>>;
  errorState: errorState;
  seterrorState: React.Dispatch<React.SetStateAction<errorState>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  dacSearchHistorySortOrder: SortOrder;
  setDacSearchHistorySortOrder: React.Dispatch<React.SetStateAction<SortOrder>>;
  dacSearchHistoryPageNum: number;
  setDacSearchHistoryPageNum: React.Dispatch<React.SetStateAction<number>>;
  dacFilterItem: FilterItem;
  setDacFilterItem: React.Dispatch<React.SetStateAction<FilterItem>>;
  dacFilterCriteria: string;
  setDacFilterCriteria: React.Dispatch<React.SetStateAction<string>>;
  dacFavoriteSortOrder: SortOrder;
  setDacFavoriteSortOrder: React.Dispatch<React.SetStateAction<SortOrder>>;
  dacFavoritePageNum: number;
  setDacFavoritePageNum: React.Dispatch<React.SetStateAction<number>>;
  splash: boolean;
  setSplash: React.Dispatch<React.SetStateAction<boolean>>;
}

export const DtsAppContext = createContext<dtsAppContextProps | null>(null);

export const useDtsAppContext = () => {
  const context = useContext(DtsAppContext);
  if (context === undefined || context === null) {
    throw new Error("error message.");
  }
  return context;
};
